import React, { useEffect, useState } from "react";
import configLoader from "../services/ConfigLoader";
import { Equipment, EquipmentStats } from "../models/EquipmentTypes";
import EquipmentTable from "../components/EquipmentTable";
import EquipmentDetail from "../components/EquipmentDetail";
import loader from "../assets/loader.gif";
import "../styles/EquipmentExplorer.style.css";

const EquipmentExplorer: React.FC = () => {
  const [equipmentByTech, setEquipmentByTech] = useState<Record<string, Equipment[]>>({});
  const [loading, setLoading] = useState(true);
  const [selectedTech, setSelectedTech] = useState<string | null>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);
  const [filters, setFilters] = useState({
    country: "",
    businessUnit: "",
    status: "",
    search: ""
  });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const data = await configLoader.loadAllEquipment();
      setEquipmentByTech(data);
      setLoading(false);
    };
    loadData();
  }, []);

  const allEquipment = selectedTech
    ? equipmentByTech[selectedTech] || []
    : Object.values(equipmentByTech).flat();

  const filteredEquipment = allEquipment.filter(eq => {
    if (filters.country && eq.location_country_code !== filters.country) return false;
    if (filters.businessUnit && eq.business_unit !== filters.businessUnit) return false;
    if (filters.status && eq.functional_object_status !== filters.status) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        eq.equipment_name?.toLowerCase().includes(search) ||
        eq.location_name?.toLowerCase().includes(search) ||
        eq.equipment_controller_id?.toLowerCase().includes(search) ||
        eq.product_family_name?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const stats: EquipmentStats = configLoader.calculateStats(allEquipment);

  const countries = Array.from(new Set(Object.values(equipmentByTech).flat().map(eq => eq.location_country_code).filter(Boolean))).sort();
  const businessUnits = Array.from(new Set(Object.values(equipmentByTech).flat().map(eq => eq.business_unit).filter(Boolean))).sort();
  const statuses = Array.from(new Set(Object.values(equipmentByTech).flat().map(eq => eq.functional_object_status).filter(Boolean))).sort();

  if (loading) {
    return (
      <div className="equipment-explorer">
        <img className="loader" src={loader} alt="Loading..." />
      </div>
    );
  }

  return (
    <div className="equipment-explorer">
      <div className="ee-header">
        <h1>Equipment Explorer</h1>
        <div className="ee-stats">
          <div className="stat-card">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total Equipments</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{Object.keys(equipmentByTech).length}</span>
            <span className="stat-label">Technologies</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{Object.keys(stats.byCountry).length}</span>
            <span className="stat-label">Countries</span>
          </div>
        </div>
      </div>

      <div className="ee-filters">
        <div className="filter-group">
          <label>Technology:</label>
          <select
            value={selectedTech || ""}
            onChange={(e) => setSelectedTech(e.target.value || null)}
          >
            <option value="">All Technologies</option>
            {Object.keys(equipmentByTech).sort().map(tech => (
              <option key={tech} value={tech}>{tech} ({equipmentByTech[tech].length})</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Country:</label>
          <select
            value={filters.country}
            onChange={(e) => setFilters(f => ({ ...f, country: e.target.value }))}
          >
            <option value="">All Countries</option>
            {countries.map(c => (
              <option key={c} value={c}>{c} ({stats.byCountry[c]})</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Business Unit:</label>
          <select
            value={filters.businessUnit}
            onChange={(e) => setFilters(f => ({ ...f, businessUnit: e.target.value }))}
          >
            <option value="">All Business Units</option>
            {businessUnits.map(bu => (
              <option key={bu} value={bu}>{bu}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
          >
            <option value="">All Statuses</option>
            {statuses.map(s => (
              <option key={s} value={s}>{s} ({stats.byStatus[s]})</option>
            ))}
          </select>
        </div>

        <div className="filter-group search">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search equipment..."
            value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
          />
        </div>
      </div>

      <div className="ee-results">
        <span>{filteredEquipment.length} equipment(s) found</span>
      </div>

      <EquipmentTable
        equipment={filteredEquipment}
        onSelect={setSelectedEquipment}
      />

      {selectedEquipment && (
        <EquipmentDetail
          equipment={selectedEquipment}
          onClose={() => setSelectedEquipment(null)}
        />
      )}
    </div>
  );
};

export default EquipmentExplorer;